import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Cookies from "js-cookie";
import moment from "moment";
import * as XLSX from "xlsx";
import { Database, Info, ShieldCheck, X } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import CommonTooltip from "../common/CommonTooltip";
import ModalCloseButton from "../common/ModalCloseButton";
import AttachProofInput from "../common/AttachProofInput";
import { toast } from "../../utils/toast";
import { getFieldNames, uploadExcel } from "../../service/Task";
import { createProtectedAccount } from "../../service/Security";
import { uploadFileToS3 } from "../../service/S3Bucket";
import { apiErrorMessage } from "../../utils/apiError";

// TEMPORARY: every account is added with this fixed location (17°01'21.3"N 54°06'07.5"E)
// until the real latitude/longitude can be supplied.
const TEMP_LATITUDE = 17.022592;
const TEMP_LONGITUDE = 54.102077;
// Same ceiling as the Create Protected Account modal.
const MAX_PROTECTED_ACCOUNTS = 250;

const normalize = (name) => String(name ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const isAccountColumn = (name) => ["accountno", "accountnumber", "accno", "accountnum", "account"].includes(normalize(name));
const isLatitudeColumn = (name) => ["latitude", "lat"].includes(normalize(name));
const isLongitudeColumn = (name) => ["longitude", "long", "lng", "lon"].includes(normalize(name));

const formatYmd = (date) => (date ? moment(date).format("YYYY-MM-DD") : null);

const inputClass =
  "h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-700 focus:border-[#236DB4] focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20";

const SectionTitle = ({ icon: Icon, title, hint }) => (
  <div className="mb-3">
    <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
      {Icon && <Icon size={16} className="text-[#236DB4]" />}
      {title}
    </h4>
    {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
  </div>
);

/**
 * Adds accounts that a protection job skipped ("not in the Master DB") to the Master DB with
 * the same account-details/excel-upload API the Import Master DB button uses, then -- when
 * "Add & Protect" is used -- sends them through create-or-update so they are protected.
 * The upload API takes a sheet, so one is built in the browser from the form.
 * When the job's accounts are already in the Master DB (alreadyInMasterDb), only protection is offered.
 */
export const AddToMasterDbModal = ({ open, onClose, accountNos = [], jobId, onDone, alreadyInMasterDb = false }) => {
  const { projectId, selectedOrgId, theme, langMode } = useContext(AppContext);
  const t = translations[langMode] || translations.en;
  const isRtl = langMode === "ar" || langMode === "ur";

  const [accounts, setAccounts] = useState([]);
  const [columns, setColumns] = useState([]); // every Master DB column, in backend order
  const [columnsState, setColumnsState] = useState("idle"); // idle | loading | ready | failed
  const [remarks, setRemarks] = useState("");
  const [permanent, setPermanent] = useState(false);
  const [fromDate, setFromDate] = useState(new Date(Date.now() + 86400000));
  const [toDate, setToDate] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  // Fresh form every time the modal opens for a job.
  useEffect(() => {
    if (!open) return;
    setAccounts([...accountNos]);
    setRemarks("");
    setPermanent(false);
    setFromDate(new Date(Date.now() + 86400000));
    setToDate(null);
    setProofFile(null);
    setTouched(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  // The Master DB columns come from the same API the Import Master DB mapping uses.
  useEffect(() => {
    if (!open || alreadyInMasterDb || columnsState === "ready") return undefined;
    let cancelled = false;
    setColumnsState("loading");
    getFieldNames()
      .then((response) => {
        if (cancelled) return;
        const value = response?.body?.value || {};
        const all = [...(value.mandatoryColumns || []), ...(value.otherColumns || [])].filter(
          (name, index, list) => name && list.indexOf(name) === index
        );
        setColumns(all);
        setColumnsState(all.length ? "ready" : "failed");
      })
      .catch(() => {
        if (!cancelled) setColumnsState("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [open, alreadyInMasterDb, columnsState]);

  const accountColumn = useMemo(() => columns.find(isAccountColumn), [columns]);
  const latitudeColumn = useMemo(() => columns.find(isLatitudeColumn), [columns]);
  const longitudeColumn = useMemo(() => columns.find(isLongitudeColumn), [columns]);
  const columnsUsable = columnsState === "ready" && accountColumn && latitudeColumn && longitudeColumn;

  const minDate = useMemo(() => new Date(Date.now() + 86400000), []);

  const removeAccount = (accountNo) => setAccounts((prev) => prev.filter((item) => item !== accountNo));

  const buildSheetFile = () => {
    const rows = accounts.map((accountNo) => {
      const row = {};
      columns.forEach((name) => {
        if (name === accountColumn) row[name] = accountNo;
        else if (name === latitudeColumn) row[name] = TEMP_LATITUDE;
        else if (name === longitudeColumn) row[name] = TEMP_LONGITUDE;
        else row[name] = "";
      });
      return row;
    });
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: columns });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Accounts");
    const bytes = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
    return new File([bytes], `master-db-accounts-job-${jobId ?? "x"}.xlsx`, {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  };

  const validate = (mode) => {
    if (!accounts.length) return t.masterDbNoAccounts;
    if (mode !== "protect" && !columnsUsable) return t.masterDbFieldsLoadFailed;
    if (mode !== "add") {
      if (accounts.length > MAX_PROTECTED_ACCOUNTS) {
        return t.protectedAccountLimitReached.replace("{limit}", MAX_PROTECTED_ACCOUNTS);
      }
      if (!remarks.trim()) return t.addremark;
      if (!permanent && (!fromDate || !toDate)) return t.pleaseSelectFromDateToDate;
    }
    return "";
  };

  // mode "add": only add the accounts to the Master DB.
  // mode "addAndProtect": add them, then send them for protection.
  // mode "protect": they are already in the Master DB, so only send them for protection.
  const handleSubmit = async (mode) => {
    setTouched(mode !== "add");
    const problem = validate(mode);
    if (problem) {
      toast.error(problem);
      return;
    }
    const protectFailed = mode === "protect" ? t.masterDbProtectFailed : t.masterDbAddedProtectFailed;
    setSubmitting(true);
    try {
      if (mode !== "protect") {
        // Mapping is { Master DB column: sheet header }; the sheet uses the column names as headers.
        const mapping = Object.fromEntries(columns.map((name) => [name, name]));
        const upload = await uploadExcel(buildSheetFile(), projectId, selectedOrgId, mapping);
        if (upload?.header?.code !== 600) {
          toast.error(apiErrorMessage(upload, t.masterDbAddFailed));
          return;
        }
      }

      if (mode === "add") {
        // The upload API answers with a generic "uploaded" text; the accounts were added by form here.
        toast.success(t.masterDbAddedSuccess);
        onDone?.({ protectionStarted: false });
        onClose();
        return;
      }

      try {
        let attachment = null;
        if (proofFile) attachment = await uploadFileToS3(proofFile);
        const response = await createProtectedAccount(
          {
            id: null,
            accountNos: accounts,
            permanent,
            fromDate: permanent ? null : formatYmd(fromDate),
            toDate: permanent ? null : formatYmd(toDate),
            editRequest: false,
            projectId,
            remarks: remarks.trim(),
            forInstalmentPlan: false,
            attachment,
          },
          Cookies.get("accessToken")
        );
        if (response?.header?.code === 600) {
          // create-or-update returns its own text in body.value.message.
          const backendMessage = response?.body?.value?.message;
          if (mode === "protect") toast.success(backendMessage || t.masterDbProtectStarted);
          else toast.success(backendMessage ? `${t.masterDbAddedSuccess} ${backendMessage}` : t.masterDbAddedAndProtected);
        } else {
          toast.warning(apiErrorMessage(response, protectFailed));
        }
      } catch (error) {
        // The Master DB part (if any) already succeeded, so say exactly what is left to do.
        const reason = apiErrorMessage(error?.response?.data) || apiErrorMessage(error);
        toast.warning(reason ? `${protectFailed} ${reason}` : protectFailed);
      }
      onDone?.({ protectionStarted: true });
      onClose();
    } catch (error) {
      // uploadExcel throws either an Error or the backend's failed envelope itself.
      toast.error(
        apiErrorMessage(error?.response?.data) || apiErrorMessage(error) || error?.message || t.masterDbAddFailed
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={submitting ? () => {} : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 z-40 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 h-full w-screen overflow-hidden">
          <div className="flex h-full items-center justify-center overflow-hidden p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                dir={isRtl ? "rtl" : "ltr"}
                className={`relative my-auto flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-full flex-col transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all sm:w-full sm:max-w-3xl ${
                  isRtl ? "text-right" : "text-left"
                } ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}`}
              >
                <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {alreadyInMasterDb ? t.masterDbProtectModalTitle : t.masterDbModalTitle}
                    </h3>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {alreadyInMasterDb ? t.masterDbProtectModalSubtitle : t.masterDbModalSubtitle}
                    </p>
                  </div>
                  <ModalCloseButton onClick={onClose} ariaLabel="Close modal" />
                </div>

                <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-4 py-5 sm:px-5">
                  <p
                    data-testid="master-db-note"
                    className="flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-[#236DB4]"
                  >
                    <Info size={14} className="mt-0.5 shrink-0" />
                    <span className="min-w-0">
                      {alreadyInMasterDb
                        ? t.masterDbAlreadyAddedNote.replace("{count}", accounts.length)
                        : t.masterDbNote
                            .replace("{count}", accounts.length)
                            .replace("{lat}", TEMP_LATITUDE)
                            .replace("{lon}", TEMP_LONGITUDE)}
                    </span>
                  </p>
                  <section>
                    <SectionTitle
                      icon={Database}
                      title={t.masterDbAccountsLabel.replace("{count}", accounts.length)}
                    />
                    <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
                      {accounts.length === 0 && <span className="px-1 text-xs text-slate-400">{t.masterDbNoAccounts}</span>}
                      {accounts.map((accountNo) => (
                        <span
                          key={accountNo}
                          className="inline-flex max-w-full items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-700"
                        >
                          <span className="min-w-0 truncate">{accountNo}</span>
                          <button
                            type="button"
                            onClick={() => removeAccount(accountNo)}
                            aria-label={t.removeAccountNumber ?? "Remove account number"}
                            className="text-slate-400 hover:text-red-500"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  </section>

                  {!alreadyInMasterDb && columnsState === "failed" && (
                    <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{t.masterDbFieldsLoadFailed}</p>
                  )}

                  <section className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
                    <SectionTitle
                      icon={ShieldCheck}
                      title={t.masterDbProtectionTitle}
                      hint={alreadyInMasterDb ? undefined : t.masterDbProtectionHint}
                    />
                    {!alreadyInMasterDb && (
                      <p className="mb-4 flex items-start gap-1.5 text-xs text-emerald-800">
                        <Info size={13} className="mt-0.5 shrink-0" />
                        {t.masterDbProtectNotice}
                      </p>
                    )}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700">
                          {t.remark} <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={remarks}
                          onChange={(e) => setRemarks(e.target.value)}
                          placeholder={t.enterremark}
                          className={`mt-1 h-20 w-full rounded-lg border px-3 py-2 text-sm text-slate-700 focus:border-[#236DB4] focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 ${
                            touched && !remarks.trim() ? "border-red-400" : "border-slate-300"
                          }`}
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={permanent}
                            onChange={(e) => setPermanent(e.target.checked)}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                          {t.permanent}
                        </label>
                        {!permanent && (
                          <div className="flex flex-wrap gap-3">
                            <DatePicker
                              selected={fromDate}
                              onChange={(date) => {
                                setFromDate(date);
                                if (toDate && date && toDate < date) setToDate(null);
                              }}
                              placeholderText={t.fromdate}
                              minDate={minDate}
                              selectsStart
                              startDate={fromDate}
                              endDate={toDate}
                              className="h-10 w-36 rounded-lg border border-slate-300 px-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              popperContainer={({ children }) => <div style={{ position: "fixed", zIndex: 100000 }}>{children}</div>}
                            />
                            <DatePicker
                              selected={toDate}
                              onChange={setToDate}
                              placeholderText={t.todate}
                              minDate={fromDate || minDate}
                              selectsEnd
                              startDate={fromDate}
                              endDate={toDate}
                              disabled={!fromDate}
                              className="h-10 w-36 rounded-lg border border-slate-300 px-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              popperContainer={({ children }) => <div style={{ position: "fixed", zIndex: 100000 }}>{children}</div>}
                            />
                          </div>
                        )}
                      </div>
                      <AttachProofInput file={proofFile} onChange={setProofFile} theme={theme} langMode={langMode} />
                    </div>
                  </section>
                </div>

                <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-5 py-4">
                  <CommonButton colorIntent="danger" variant="danger" onClick={onClose} disabled={submitting}>
                    {t.cancel}
                  </CommonButton>
                  {alreadyInMasterDb ? (
                    <CommonTooltip
                      text={t.masterDbSubmitProtectTooltip}
                      position="top"
                      disableDefaultDescription
                      tooltipClassName="w-max max-w-[min(15rem,calc(100vw-1.5rem))] [text-wrap:balance]"
                      fitContent
                      clampToViewport
                      hoverOnly
                    >
                      <CommonButton
                        colorIntent="success"
                        variant="primary"
                        onClick={() => handleSubmit("protect")}
                        disabled={submitting || !accounts.length}
                        data-testid="master-db-protect"
                      >
                        {submitting ? t.Processing : t.masterDbSubmitProtect}
                      </CommonButton>
                    </CommonTooltip>
                  ) : (
                    <>
                      <CommonTooltip
                        text={t.masterDbSubmitAddTooltip}
                        position="top"
                        disableDefaultDescription
                        tooltipClassName="w-max max-w-[min(15rem,calc(100vw-1.5rem))] [text-wrap:balance]"
                        fitContent
                        clampToViewport
                        hoverOnly
                      >
                        <CommonButton
                          colorIntent="primary"
                          variant="primary"
                          onClick={() => handleSubmit("add")}
                          disabled={submitting || columnsState === "loading" || !accounts.length}
                          data-testid="master-db-add"
                        >
                          {submitting ? t.Processing : t.masterDbSubmitAdd}
                        </CommonButton>
                      </CommonTooltip>
                      <CommonTooltip
                        text={t.masterDbSubmitAddAndProtectTooltip}
                        position="top"
                        disableDefaultDescription
                        tooltipClassName="w-max max-w-[min(15rem,calc(100vw-1.5rem))] [text-wrap:balance]"
                        fitContent
                        clampToViewport
                        hoverOnly
                      >
                        <CommonButton
                          colorIntent="success"
                          variant="primary"
                          onClick={() => handleSubmit("addAndProtect")}
                          disabled={submitting || columnsState === "loading" || !accounts.length}
                          data-testid="master-db-submit"
                        >
                          {submitting ? t.Processing : t.masterDbSubmitAddAndProtect}
                        </CommonButton>
                      </CommonTooltip>
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddToMasterDbModal;
